/**
 * Didomi CMP loader — everything about injecting the Web SDK that is not tied to
 * one app.
 *
 * Shared by `customer-app` and `amplify-auth` on purpose: both serve the same
 * brands from the same root domains, so the proxy list and the notice-language
 * table must not be allowed to drift. A brand added on one side only would
 * silently lose its banner to tracker blockers on the other.
 *
 * Framework-agnostic — no Vue, no i18n. Each app resolves its own API key,
 * notice id and locale from its build env, then hands them over.
 */

/**
 * Subset of the Didomi Web SDK surface our apps actually call.
 *
 * Documentation: https://developers.didomi.io/cmp/web-sdk/reference/api
 */
export interface DidomiSdk {
  /** Preferences center (the "manage my choices" modal). */
  preferences: {
    /** Opens the preferences center, optionally on a specific view. */
    show: (view?: 'purposes' | 'vendors') => void;
    hide: () => void;
    isVisible: () => boolean;
  };
  /** First-layer consent notice (the banner). */
  notice: {
    show: () => void;
    hide: () => void;
    isVisible: () => boolean;
  };
  /** Enable consent for every purpose and vendor. */
  setUserAgreeToAll: () => void;
  /** Disable consent for every purpose and vendor. */
  setUserDisagreeToAll: () => void;
  /** True while consent still has to be collected from this user. */
  isConsentRequired: () => boolean;
  /** Subscribe to an SDK event (`consent.changed`, `notice.shown`, …). */
  on: (event: string, listener: (...args: unknown[]) => void) => void;
}

/**
 * Loader configuration. MUST be assigned to `window.didomiConfig` **before**
 * the loader script runs — Didomi reads it once at initialization.
 *
 * Documentation: https://developers.didomi.io/cmp/web-sdk/reference/configuration-by-javascript
 */
export interface DidomiConfig {
  /**
   * Origin the SDK loads its own assets from, e.g.
   * `https://privacy.santevet.com/`. **Trailing slash required** — Didomi
   * concatenates paths onto it without normalising.
   *
   * Set to our reverse proxy so the SDK is served first-party (CXP-2520).
   * Derive it with `toDidomiProxyOrigin` rather than by hand — a host with no
   * distribution must keep Didomi's CDN, since a failed SDK fetch means no
   * consent banner at all.
   */
  sdkPath?: string;
  /**
   * Origin for consent API calls, e.g. `https://privacy.santevet.com/api`.
   * **No trailing slash** (unlike `sdkPath`).
   */
  apiPath?: string;
  languages?: {
    /**
     * BCP 47 language codes the notice is allowed to render in. May be
     * regional — the brand notices declare `fr-BE` and `nl-BE` alongside plain
     * `fr` / `de` / `es` / `it`, so do NOT assume 2-letter codes. Derive values
     * from `LOCALE_TO_DIDOMI_LANGUAGE` rather than by hand: a code the notice
     * does not declare falls back to the notice default, which is how `nl-BE`
     * would silently become French.
     */
    enabled?: string[];
    /**
     * BCP 47 code used when the visitor's language is absent from `enabled`. A
     * fallback, NOT an override — to force a language, restrict `enabled` to
     * that single code (see `injectDidomiLoader`).
     */
    default?: string;
  };
  notice?: {
    /** Set to false to suppress notice display entirely (used by E2E). */
    enable?: boolean;
  };
  [key: string]: unknown;
}

/**
 * Marks the script tag we inject so we can detect an existing injection without
 * depending on the loader URL shape.
 */
export const DIDOMI_SCRIPT_SELECTOR = 'script[data-cda-didomi]';

/** `dataset` key behind {@link DIDOMI_SCRIPT_SELECTOR}. */
const DIDOMI_SCRIPT_DATASET_KEY = 'cdaDidomi';

/**
 * Didomi's own CDN. Used only when the current host has no reverse proxy — see
 * `toDidomiProxyOrigin`.
 */
export const DIDOMI_DIRECT_SDK_ORIGIN = 'https://sdk.privacy-center.org';

/**
 * Root domains fronted by the Didomi reverse proxy (DSO-1424 / CXP-2520): one
 * CloudFront distribution per domain, serving the SDK and consent API
 * first-party from `privacy.<root-domain>`.
 *
 * Serving the SDK from our own domain is the whole point — it survives tracker
 * blockers that drop `sdk.privacy-center.org`, so the notice still appears.
 */
export const DIDOMI_PROXIED_ROOT_DOMAINS = [
  'santevet.com',
  'santevet.be',
  'santevet.de',
  'santevet.es',
  'santevet.it',
  'bullebleue.fr',
  'jim-joe.fr',
] as const;

const isProxiedRootDomain = (domain: string) =>
  (DIDOMI_PROXIED_ROOT_DOMAINS as readonly string[]).includes(domain);

/**
 * Proxy origin for a host, or `null` when that host has no distribution.
 *
 * The root domain is the last two labels, which is what makes this work across
 * environments without an extra env var: the customer space runs on
 * `espaceclient.santevet.com` in production and
 * `espaceclient.dev.aws.santevet.com` in dev/preprod, and both resolve to the
 * same `privacy.santevet.com`. That is intended — the proxy is
 * environment-agnostic (it fronts Didomi's CDN with the API key that is already
 * shared across our environments).
 *
 * Returning `null` rather than guessing is deliberate: on `localhost` or a
 * review app hosted elsewhere, `privacy.<host>` does not resolve, and a failed
 * SDK fetch means **no consent banner at all**. Those hosts keep the direct CDN.
 */
export const toDidomiProxyOrigin = (hostname?: string): string | null => {
  if (!hostname) return null;

  const rootDomain = hostname.toLowerCase().split('.').slice(-2).join('.');

  return isProxiedRootDomain(rootDomain)
    ? `https://privacy.${rootDomain}`
    : null;
};

/**
 * Language codes the brand notices actually declare. Verified against the live
 * notice configs on 2026-07-30:
 *
 * - santevet   (`anREjxbY`) → fr, es, de, it, fr-BE, nl-BE
 * - bullebleue (`9TqByGyQ`) → fr
 * - jimetjoe   (`bgZC66t3`) → fr
 *
 * Note `nl` and `en` are NOT provisioned, and Belgium is regional (`fr-BE` /
 * `nl-BE`) because it carries its own legal text.
 */
export const DIDOMI_LANGUAGES = [
  'fr',
  'fr-BE',
  'nl-BE',
  'de',
  'es',
  'it',
] as const;

export type DidomiLanguage = (typeof DIDOMI_LANGUAGES)[number];

/** Every brand notice declares `fr` as its default. */
const DEFAULT_DIDOMI_LANGUAGE: DidomiLanguage = 'fr';

/**
 * App locale → Didomi language code.
 *
 * Deliberately an explicit table rather than "drop the region": the two are not
 * interchangeable. Belgium keeps its region (`nl-BE`, `fr-BE`) because it has
 * its own notice translation, while Germany, Spain and Italy collapse to 2
 * letters. Deriving the code instead would send `nl-BE` to a non-existent `nl`
 * and silently serve Dutch-speaking Belgians a French notice.
 *
 * Keys are the locales the apps can actually resolve to. Both build theirs the
 * same way — `<language>-<country>` (customer-app's `buildLocaleString` and
 * amplify-auth's namesake helper) — so `fr-FR`, `fr-BE`, `nl-BE`, `de-DE`,
 * `es-ES` and `it-IT` cover every deployment. Bulle Bleue and Jim & Joe resolve
 * to `fr-FR` and get `fr` on their own single-language notice; `fr-BB` / `fr-JJ`
 * name customer-app translation files, not locales, and are mapped only
 * defensively.
 */
const LOCALE_TO_DIDOMI_LANGUAGE: Record<string, DidomiLanguage> = {
  'fr-FR': 'fr',
  'fr-BB': 'fr',
  'fr-JJ': 'fr',
  'fr-BE': 'fr-BE',
  'nl-BE': 'nl-BE',
  'de-DE': 'de',
  'es-ES': 'es',
  'it-IT': 'it',
};

const isDidomiLanguage = (value: string): value is DidomiLanguage =>
  (DIDOMI_LANGUAGES as readonly string[]).includes(value);

/** `fr-fr` / `FR-fr` → `fr-FR`, so casing from the app can't miss the table. */
const normalizeLocale = (locale: string) => {
  const [language, region] = locale.split('-');
  return region
    ? `${language.toLowerCase()}-${region.toUpperCase()}`
    : language.toLowerCase();
};

/**
 * Maps an app's resolved locale onto the language code its notice is translated
 * for, falling back to the notices' own default (`fr`).
 */
export const toDidomiLanguage = (locale?: string): DidomiLanguage => {
  if (!locale) return DEFAULT_DIDOMI_LANGUAGE;

  const normalized = normalizeLocale(locale);
  const mapped = LOCALE_TO_DIDOMI_LANGUAGE[normalized];
  if (mapped) return mapped;

  // An app can hand back a bare language ('fr'); accept it when a notice
  // declares it, otherwise take the default rather than guessing a region.
  return isDidomiLanguage(normalized) ? normalized : DEFAULT_DIDOMI_LANGUAGE;
};

/**
 * How long a caller should wait for the SDK before giving up on
 * {@link onDidomiReady} and proceeding without consent defaults.
 *
 * `didomiOnReady` is never drained when the loader itself fails — a tracker
 * blocker that slips past the first-party proxy, a CloudFront outage — and
 * without a deadline that takes analytics down with the CMP for a whole
 * session. Generous on purpose: it must only fire when the SDK is genuinely
 * absent, never in a race with a slow-but-working load.
 *
 * Lives here rather than in either app because both wait on the same queue, and
 * a value that disagreed between them would be a silent inconsistency in how
 * long each trusts the same SDK.
 */
export const DIDOMI_READY_TIMEOUT_MS = 5000;

/** True once a loader script has been injected into this document. */
export const isDidomiLoaderInjected = () =>
  typeof document !== 'undefined' &&
  document.querySelector(DIDOMI_SCRIPT_SELECTOR) !== null;

export interface DidomiLoaderOptions {
  /**
   * `VITE_APP_DIDOMI_API_KEY`. Shared across brands and environments. Optional
   * so callers can pass a build env straight through: a missing key is reported
   * rather than injected, which is what lets each app fall back (to the legacy
   * CMP, or to loading GTM unguarded as before).
   */
  apiKey?: string;
  /**
   * `VITE_APP_DIDOMI_NOTICE_ID`. Each brand is a separate build, so one notice
   * id per deployment covers both the "one notice per brand" and "one shared
   * notice" setups. When unset, Didomi resolves the notice from the request
   * domain instead.
   */
  noticeId?: string;
  /** Locale the app is rendered in, e.g. `nl-BE`. See `toDidomiLanguage`. */
  locale?: string;
  /** Host to derive the proxy origin from. Defaults to the current host. */
  hostname?: string;
}

/** Builds the loader URL for a notice, served from `origin`. */
const getLoaderUrl = (
  origin: string,
  apiKey: string,
  noticeId: string | undefined,
) => {
  const url = new URL(`${origin}/${apiKey}/loader.js`);
  if (noticeId) {
    url.searchParams.set('target_type', 'notice');
    url.searchParams.set('target', noticeId);
  }
  return url.toString();
};

/**
 * Injects the Didomi loader, configuring it for this deployment first.
 *
 * Returns whether a loader is in the DOM afterwards, so callers can fall back
 * to another CMP (or skip the GTM consent-mode handoff) when it is not.
 */
export const injectDidomiLoader = ({
  apiKey,
  noticeId,
  locale,
  hostname,
}: DidomiLoaderOptions): boolean => {
  // Ensure we're running in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  if (!apiKey) {
    console.error('No Didomi API key found in environment variables.');
    return false;
  }

  const head = document.head;
  if (!head) {
    console.error('Document head not found. Unable to add Didomi script.');
    return false;
  }

  // The loader is idempotent from our side: re-injecting it would re-run the
  // SDK bootstrap and can flash the notice a second time.
  if (isDidomiLoaderInjected()) {
    return true;
  }

  // The notice must match the language the app is rendered in. The app has
  // already resolved its locale by the time this is called, and that decision
  // wins over the visitor's browser language — a German browser on the fr-FR
  // site gets a French app, so it must get a French notice.
  //
  // `languages.default` alone does NOT achieve that: Didomi treats it as the
  // fallback used only when the browser language is absent from
  // `languages.enabled`. Restricting `enabled` to the single resolved language
  // is the documented way to force it ("if you only enable one language, the
  // visitor can't get any other translation") — which is also why the code has
  // to be one the notice really declares, see LOCALE_TO_DIDOMI_LANGUAGE.
  const language = toDidomiLanguage(locale);

  // Both the loader and the SDK's own subsequent requests have to go through the
  // proxy: the loader URL below covers the first fetch, `sdkPath` / `apiPath`
  // cover everything the SDK loads afterwards. Setting only one of the two
  // leaves half the traffic on the blockable CDN.
  const proxyOrigin = toDidomiProxyOrigin(hostname ?? window.location.hostname);

  // MUST happen before the loader is added to the DOM — Didomi reads this once
  // at init. Merged rather than replaced so an earlier assignment (E2E's
  // `notice.enable: false`, or a GTM-side config) survives.
  window.didomiConfig = {
    ...window.didomiConfig,
    // Omitted entirely off-proxy so the SDK keeps its own defaults; the paths
    // are load-bearing strings, hence the exact slashes required by Didomi.
    ...(proxyOrigin && {
      sdkPath: `${proxyOrigin}/`, // trailing slash REQUIRED
      apiPath: `${proxyOrigin}/api`, // NO trailing slash
    }),
    languages: {
      ...window.didomiConfig?.languages,
      enabled: [language],
      default: language,
    },
  };

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = getLoaderUrl(
    proxyOrigin ?? DIDOMI_DIRECT_SDK_ORIGIN,
    apiKey,
    noticeId,
  );
  script.async = true;
  script.charset = 'utf-8';
  script.dataset[DIDOMI_SCRIPT_DATASET_KEY] = 'true';

  script.onerror = (error) => {
    console.error('Failed to load Didomi script:', error);
  };

  try {
    head.prepend(script);
  } catch (error) {
    console.error('Error adding Didomi script to head:', error);
    return false;
  }

  return true;
};

/**
 * Queues a callback for when the SDK is ready, or runs it now if it already is.
 *
 * `didomiOnReady` is the supported way to call the API without knowing whether
 * the SDK has loaded yet — the loader drains the array once initialization
 * completes. Returns false when no loader has been injected at all (flag off,
 * or a build with no credentials), so callers don't wait on a queue that will
 * never drain.
 */
export const onDidomiReady = (
  callback: (didomi: DidomiSdk) => void,
): boolean => {
  if (typeof window === 'undefined') return false;

  if (window.Didomi) {
    callback(window.Didomi);
    return true;
  }

  if (!isDidomiLoaderInjected()) return false;

  window.didomiOnReady = window.didomiOnReady ?? [];
  window.didomiOnReady.push(callback);
  return true;
};

declare global {
  interface Window {
    /** Present only once the SDK has finished initializing. */
    Didomi?: DidomiSdk;
    didomiConfig?: DidomiConfig;
    /**
     * Callback queue drained by the SDK on init. Go through `onDidomiReady`
     * rather than pushing directly.
     */
    didomiOnReady?: Array<(didomi: DidomiSdk) => void>;
    didomiEventListeners?: unknown[];
  }
}
